import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Tween, Easing } from '@tweenjs/tween.js'
import i18next from 'i18next'
import Currency from 'macguffin/Utils/Currency'

const EXPAND_SCALE = 1.25
const EXPAND_DURATION_QUOTE = 0.333
const EXPAND_EASING = Easing.Quadratic.InOut

const PULSE_SCALE = 1.5
const PULSE_DURATION_QUOTE = 0.333
const PULSE_EASING = Easing.Back.InOut

const SHRINK_SCALE = 1
const SHRINK_DELAY_QUOTE = 0.166
const SHRINK_DURATION_QUOTE = 0.166
const SHRINK_EASING = Easing.Quadratic.InOut

const InfoArea = props => {
  const {
    value = 0,
    formatStyle,
    dimensions: { desktopUI, portrait },
    name,
    currencySign,
    prefix,
    showCurrency,
    autoPlayVisible,
    duration,
    paused,
  } = props
  const previousProps = useRef(props)
  const lastValue = useRef(null)
  const scaleUpTween = useRef(null)
  const scaleDownTween = useRef(null)
  const pulseTween = useRef(null)
  const valueElement = useRef(null)
  const valueCurrencyElement = useRef(null)

  const setValue = value => valueElement.current.textContent = Currency.format(value, formatStyle)

  useEffect(() => {
    setValue(value)
    return () => stopTweens(previousProps.current.value)
  }, [])

  useEffect(() => {
    const { current: prevProps } = previousProps
    if (props.value !== prevProps.value || props.dropCount !== prevProps.dropCount) {
      if (!props.dropCount && props.value > prevProps.value) {
        if (props.skipfeature) {
          stopTweens(props.value)
          startScaleDown().start()
        } else {
          countUp(prevProps.value, props.value)
        }
      } else {
        stopTweens(props.value || 0)
      }
    }

    if (props.currencyChanged !== prevProps.currencyChanged) {
      setValue(value)
    }
    if (props.skipfeature && scaleUpTween.current &&
      props.skipfeature !== prevProps.skipfeature) {
      stopTweens(props.value)
      startScaleDown().start()
    }
    previousProps.current = props
  })

  useEffect(() => {
    if (paused) {
      stopTweens(props.value)
    }
  }, [paused])

  const countUp = (oldValue, newValue) => {
    lastValue.current = newValue
    stopTweens(oldValue)

    scaleUpTween.current = new Tween({ scale: SHRINK_SCALE, value: oldValue })
      .to({ scale: EXPAND_SCALE, value: newValue }, EXPAND_DURATION_QUOTE * duration)
      .easing(EXPAND_EASING)
      .onUpdate(onTweenUpdate)

    pulseTween.current = new Tween({ scale: SHRINK_SCALE })
      .to({ scale: PULSE_SCALE }, PULSE_DURATION_QUOTE * duration)
      .easing(PULSE_EASING)
      .onUpdate(onTweenUpdate)

    scaleDownTween.current = startScaleDown()

    scaleUpTween.current
      .chain(pulseTween.current)
      .chain(scaleDownTween.current)
      .start()
  }

  const onTweenUpdate = tween => {
    if (!valueCurrencyElement.current) { return }
    if (tween.value) {
      setValue(tween.value)
    }
    valueCurrencyElement.current.style.transform = `scale(${tween.scale})`
  }

  const startScaleDown = () => {
    scaleDownTween.current = new Tween({ scale: PULSE_SCALE })
      .to({ scale: SHRINK_SCALE }, SHRINK_DURATION_QUOTE * duration)
      .delay(SHRINK_DELAY_QUOTE * duration).easing(SHRINK_EASING)
      .onUpdate(onTweenUpdate)
      .onComplete(() => setValue(value))

    return scaleDownTween.current
  }

  // we must stop all possible previous animations....
  const stopTweens = forceValue => {
    if (scaleUpTween.current) {
      scaleUpTween.current.stop()
      scaleUpTween.current = null
    }
    if (pulseTween.current) {
      pulseTween.current.stop()
      pulseTween.current = null
    }
    if (scaleDownTween.current) {
      scaleDownTween.current.stop()
      scaleDownTween.current = null
    }

    valueCurrencyElement.current.style.transform = 'scale(1)'
    const valueOnStop = isNaN(forceValue) ? lastValue.current : forceValue
    setValue(valueOnStop)
  }

  let sizeClass = Currency.format(value, formatStyle).length > 10 ? 'bigValue' : ''
  const displayedLabel = i18next.t(`${prefix}.${name}`)
  let textSizeClass = displayedLabel && displayedLabel.length >= 16 ? 'bigText' : ''
  if (!desktopUI && !portrait && (sizeClass || textSizeClass)) {
    sizeClass = 'bigValue'
    textSizeClass = 'bigText'
  }
  return (
    <div className={`${name} info ${autoPlayVisible ? '' : 'no-autoplay'}`}>
      <div className="bg-field" />
      <div className={`infoArea ${sizeClass}`} ref={valueCurrencyElement}>
        {showCurrency && (!desktopUI && !portrait) && <span className="text">{currencySign}</span>}
        <span className="text" ref={valueElement}></span>
      </div>
      <div className={`labels ${textSizeClass}`} >
        <div className="label" id={`${prefix}_${name}_label`}>
          {displayedLabel}
        </div>
        {showCurrency && (desktopUI || portrait) && <div className="label currency">{currencySign}</div>}
      </div>
    </div>
  )
}

export default connect(state => ({
  currencyChanged: state.base.currencyChanged,
  dimensions: state.UI.dimensions,
  paused: state.base.paused,
}))(InfoArea)